import { useMemo } from "react";
import { useServices } from "@core/servicesContext";
import { StorePresentation } from "@core/store/storePresentation";
import {
	AsyncResult,
	PaginatedDataResult,
	PromiseResult,
	useMappedStore,
	usePaginatedStore,
	usePromise,
	useStoredData,
} from "@utils/hooks";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Category } from "@domain/lms/category/category";
import { Article, ArticleStatus } from "./article";
import { DetailedArticle } from "./dtos";
import { Feedback } from "@domain/lms/article/feedback/feedback";

export function useArticle(id: string, slug?: string): AsyncResult<Article> {
	const { articleService } = useServices();
	const currentProfile = useCurrentProfile();

	return useStoredData(slug ?? id, slug ? articleService.slugArticleStore : articleService.articleStore, [
		currentProfile?.id,
		slug,
	]);
}

export function useFavoriteArticles(userId: string): PaginatedDataResult<Article> {
	const { articleService } = useServices();
	return usePaginatedStore(articleService.favoriteArticlesPresentation, [userId]);
}

export function useMyArticles(status: ArticleStatus | "ALL"): PaginatedDataResult<Article> {
	const { articleService } = useServices();
	return useMappedStore(status, articleService.myArticlesStore, [status]);
}

export function usePersonArticles(personId: string, organization?: boolean): PaginatedDataResult<Article> {
	const { articleService } = useServices();
	return useMappedStore(
		personId,
		organization ? articleService.organizationArticlesStore : articleService.profileArticlesStore,
		[personId, organization]
	);
}

export function useSuggestedArticles(
	count: number,
	cat: Category | Category[] | undefined
): PaginatedDataResult<Article> {
	const { articleService } = useServices();

	const store = useMemo(
		() => new StorePresentation(articleService.articleStore, () => articleService.suggest(count, cat)),
		[count, cat]
	);
	return usePaginatedStore(store, [count]);
}
export function useNewArticles(count: number, page: number): PaginatedDataResult<Article> {
	const { articleService } = useServices();

	const store = useMemo(
		() => new StorePresentation(articleService.articleStore, () => articleService.newsArticles(count, page)),
		[count, page]
	);
	return usePaginatedStore(store, [count, page]);
}

export function useArticleFeedbacks(postId: string): PaginatedDataResult<Feedback> {
	const { articleService } = useServices();
	return useMappedStore(postId, articleService.feedbackStore, [postId]);
}

export function useMyFeedback(articleId: string, articleFeedbackId: string): PromiseResult<Feedback | null> {
	const { articleService } = useServices();
	return usePromise(() => articleService.getMyFeedback(articleId, articleFeedbackId), [articleId, articleFeedbackId]);
}

export function useDetailedArticle(id: string, slug?: string): AsyncResult<DetailedArticle> {
	const { articleService } = useServices();
	const currentProfile = useCurrentProfile();

	return useStoredData(
		slug ?? id,
		slug ? articleService.detailedArticleStoreBySlug : articleService.detailedArticleStore,
		[currentProfile?.id, slug]
	);
	//return useStoredData(id, articleService.detailedArticleStore, [currentProfile?.id]);
}

export function useWorkspaceArticles(workspaceId: string): PaginatedDataResult<Article> {
	const { articleService } = useServices();
	return useMappedStore(workspaceId, articleService.workspaceArticlesStore, [workspaceId]);
}
